<script>
	// NPM Modules
	import dayjs from 'dayjs';
	import localizedFormat from 'dayjs/plugin/localizedFormat';
	import advancedFormat from 'dayjs/plugin/advancedFormat';
	import utc from 'dayjs/plugin/utc';
	import timezone from 'dayjs/plugin/timezone';
	// Constants
	import { PromotionsType } from '@/Constants/Promotions.js';
	import { UserRatingName, UserStatusName, UserStageName, UserSubRatingName } from '@/Constants/User.js';
	// Components
	import Modal from '@/Components/Modal.svelte';
	// Stores
	import CampaignStore from '@/Stores/CampaignStore.js';
	// Services
	import { CurrencyFormat } from '@/Services/Helpers.js';

	dayjs.extend(localizedFormat);
	dayjs.extend(advancedFormat);
	dayjs.extend(utc);
	dayjs.extend(timezone);

	export let campaignId;
	export let open = false;
	export let account_managers = [];
	export let campaign = $CampaignStore.firstWhere('id', campaignId);

	function getListText(value, listType) {
		let stringOfText = '';
		for (const listValue of value) {
			if (stringOfText !== '') {
				stringOfText = stringOfText + ', ';
			}
			if (listType == 'rating') {
				stringOfText = stringOfText + UserRatingName[listValue];
			} else if (listType == 'account_status') {
				stringOfText = stringOfText + UserStatusName[listValue];
			} else if (listType == 'sub_rating') {
				stringOfText = stringOfText + UserSubRatingName[listValue];
			} else if (listType == 'life_stage') {
				if (listValue == null) {
					stringOfText = stringOfText + 'No Stage';
				} else {
					stringOfText = stringOfText + UserStageName[listValue];
				}
			} else if (listType == 'client_category') {
				stringOfText = stringOfText + getAccountManagerName(listValue);
			} else if (listType == 'client_state') {
				stringOfText = stringOfText + listValue.toLocaleString();
			}
		}
		return stringOfText;
	}

	function getAccountManagerName(accountManagerId) {
		if (!parseInt(accountManagerId)) {
			return accountManagerId[0].toUpperCase() + accountManagerId.substring(1);
		}
		for (const manager of Object.keys(account_managers)) {
			if (manager == accountManagerId) {
				return 'Managed by ' + account_managers[manager];
			}
		}
		return undefined;
	}

	function getListValueDecorator(criteria, value) {
		if (
			['rating', 'account_status', 'client_category', 'client_state', 'sub_rating', 'life_stage'].includes(criteria)
		) {
			return getListText(Object.values(value), criteria);
		} else if (
			[
				'balance',
				'average_bet_size',
				'lifetime_net_win',
				'greatest_amount_deposited',
				'remaining_deposit_limit',
				'net_deposits_7_days',
				'net_deposits_14_days',
				'net_deposits_30_days',
				'net_deposits_90_days',
				'net_deposits_lifetime',
				'total_withdrawals_7_days'
			].includes(criteria)
		) {
			return CurrencyFormat(value);
		} else if (['bet_count', 'pending_bets'].includes(criteria)) {
			if (value == 1) {
				return value + ' bet';
			}
			return value + ' bets';
		} else if (
			[
				'net_win_margin_lifetime',
				'net_win_margin_90_days',
				'bonus_cost_percentage',
				'bonus_cost_gross_win_ratio_lifetime',
				'bonus_cost_gross_win_ratio_90_days'
			].includes(criteria)
		) {
			return value + '%';
		} else if (['last_bet_made_in', 'last_deposit_made', 'last_withdrawal_made'].includes(criteria)) {
			if (value == 1) {
				return value + ' day';
			}
			return value + ' days';
		} else if (['last_transaction'].includes(criteria)) {
			if (value == 'deposit_or_withdrawal') {
				return 'Deposit or withdrawal';
			}
			return value[0].toUpperCase() + value.substring(1);
		} else if (['has_deposit_limit'].includes(criteria)) {
			if (value) {
				return 'Yes';
			}
			return 'No';
		} else {
			return value;
		}
	}

	function getListHeadingText(criteria) {
		return (criteria[0].toUpperCase() + criteria.substring(1)).replaceAll('_', ' ');
	}
</script>

{#if open && campaign}
	<Modal on:close={() => (open = false)} className="md:w-4/5 lg:w-3/4 mx-2 md:mx-auto">
		<h2 slot="header" class="pt-2 text-xl font-bold text-center">
			Campaign: {campaign.campaign_id} - {campaign.name}
		</h2>
		<div class="px-4 pb-4">
			<hr class="my-2" />
			<div class="flex flex-col">
				<div class="flex flex-row">
					<div class="flex flex-row w-1/2">
						<div class="flex flex-col space-y-4">
							<div class="flex flex-col">
								<h1 class="font-semibold text-lg mb-3">Campaign:</h1>
								<span>ID: <span class="font-semibold">{campaign.campaign_id}</span></span>
								<span>Name: <span class="font-semibold">{campaign.name}</span></span>
								<span>Status: <span class="font-semibold">{campaign.status.name}</span></span>
								<span>Created By: <span class="font-semibold">{campaign.created_by.name}</span></span>
								{#if campaign.approved_by.id !== undefined}
									<span>Approved By: <span class="font-semibold">{campaign.approved_by.name}</span></span>
									<span>Approval Status: <span class="font-semibold">{campaign.approval_status.name}</span></span>
								{/if}
							</div>
							<hr />
							<div class="flex flex-col">
								<h1 class="font-semibold text-lg mb-3">Schedule:</h1>
								<span
									>Campaign Start: <span class="font-semibold"
										>{dayjs(campaign.publish_date + ' ' + campaign.time_to_start).format('LLLL z')}</span
									></span
								>
								<span
									>Campaign End: <span class="font-semibold"
										>{dayjs(campaign.end_date + ' ' + campaign.time_to_start).format('LLLL z')}</span
									></span
								>
								<span
									>Refresh user list: <span class="font-semibold">{campaign.refresh_user_list ? 'Yes' : 'No'}</span
									></span
								>
								<span>Repeat interval: <span class="font-semibold">{campaign.repeat_type.name}</span></span>
								<span
									>Expires after: <span class="font-semibold"
										>{campaign.expires_in === 0
											? campaign.expires_in_unit.name
											: campaign.expires_in + ' ' + campaign.expires_in_unit.name}</span
									></span
								>
							</div>
							<hr />
							<div class="flex flex-col">
								<h1 class="font-semibold text-lg mb-3">Audience:</h1>
								<div class="flex flex-col">
									{#each Object.keys(campaign.user_criteria) as criteria}
										<div class="flex flex-row space-x-1 items-center">
											<span class="font-semibold">{getListHeadingText(criteria)}</span>
											<span class="whitespace-nowrap px-1">is {campaign.user_criteria[criteria].operator}</span>
											<span class="font-semibold"
												>{getListValueDecorator(criteria, campaign.user_criteria[criteria].value)}</span
											>
											{#if campaign.user_criteria[criteria].value_between}
												<span>and</span>
												<span class="font-semibold"
													>{getListValueDecorator(criteria, campaign.user_criteria[criteria].value_between)}</span
												>
											{/if}
										</div>
									{/each}
								</div>
							</div>
							<hr />
							<div class="flex flex-col">
								<h1 class="font-semibold text-lg mb-3">Offer details:</h1>
								{#if campaign.type.id === PromotionsType.NextDepositMatch}
									<span
										>Bonus valid days: <span class="font-semibold">{campaign.offer_rules.bonus_valid_days} days</span
										></span
									>
									<span
										>Max deposit amount: <span class="font-semibold"
											>{CurrencyFormat(campaign.offer_rules.max_bonus_amount)}</span
										></span
									>
									<span
										>Max deposit percent: <span class="font-semibold"
											>{campaign.offer_rules.max_payout_percentage}%</span
										></span
									>
								{/if}
							</div>
						</div>
					</div>
					<div class="flex flex-row w-1/2">
						<div class="flex flex-col">
							<!--<h1 class="font-semibold text-lg mb-3">Comments:</h1>
							<div class="flex flex-row">Add new comment:</div>-->
						</div>
					</div>
				</div>
			</div>
		</div>
	</Modal>
{/if}
